<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { VButton, VButtonGroup, VTextArea } from '@/modules/shared/components'
import { type Check } from '../types'
import { useCheckStore } from '../stores/check-store'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const modelValue = defineModel<Check>()

const store = useCheckStore()
const isReviewReopened = ref(false)

const initialState = {
  note: null,
}
const rules = {
  note: { required },
}
const form = ref({ ...initialState })
const v$ = useVuelidate(rules, form, { $lazy: true })

watch(
  () => modelValue,
  (newCheck) => {
    form.value.note = newCheck.value?.note || ''
  },
)

const description = 'You must leave a note when dismissing a false positive'
const error = computed(() => (v$.value['note']?.$errors[0]?.$message ? description : null))

const canReopenReview = computed(() => {
  return ['confirmed_match', 'false_positive'].includes(modelValue.value?.status) && !isReviewReopened.value
})

const reopenReview = async () => {
  isReviewReopened.value = true
  form.value.note = modelValue.value?.note || ''
}

const canConfirmMatch = computed(() => {
  return modelValue.value?.status !== 'confirmed_match' || form.value.note !== modelValue.value?.note
})

const confirmMatch = async () => {
  await store.confirmMatch(modelValue.value?.id, form.value.note)
  modelValue.value = await store.fetchCheck(modelValue.value?.id)
  isReviewReopened.value = false
}

const canDismissFalsePositive = computed(() => {
  return modelValue.value?.status !== 'false_positive' || form.value.note !== modelValue.value?.note
})

const dismissFalsePositive = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return
  await store.dismissFalsePositive(modelValue.value?.id, form.value.note)
  modelValue.value = await store.fetchCheck(modelValue.value?.id)
  isReviewReopened.value = false
}
</script>

<template>
  <div>
    <div v-if="canReopenReview">
      <VButton @click="reopenReview" class="w-full">Reopen Review</VButton>
    </div>
    <div v-else>
      <div class="mb-2">
        <VTextArea
          v-model="form.note"
          :description="error ? null : description"
          label="Note / Remarks"
          :required="true"
          :error="error"
        />
      </div>
      <VButtonGroup>
        <VButton :click="confirmMatch" :disabled="!canConfirmMatch">Confirm Match</VButton>
        <VButton :click="dismissFalsePositive" :disabled="!canDismissFalsePositive">Dismiss False Positive</VButton>
      </VButtonGroup>
    </div>
  </div>
</template>
